// Client date helpers. The app renders in ONE zone — the user's home timezone
// (the `displayTimezone` setting, read via useTz()); see docs/timezones.md.
//
// Two kinds of value live here, and the distinction is the whole game:
//   • a CIVIL DATE — a calendar day with no instant, represented as a Date at
//     LOCAL midnight purely as a carrier for its Y/M/D (the month-grid cells,
//     the selected day). The grid helpers below do pure calendar arithmetic on
//     these and need no zone.
//   • an INSTANT — an ISO/UTC timestamp (an event start, a due date, a feed
//     time). Anything that turns an instant into a day or a label MUST be given
//     the home zone, so it renders the same everywhere regardless of the browser.
//
// This module (with `timezone.tsx` and planning's `ui/lib/events.ts`) is the sanctioned home
// for the client's date logic — feature code calls these, never raw `Date`
// field math (a convention — see docs/timezones.md).

import { APP_LOCALE } from "@/platform/kit";
import { isoDayInZone, utcToZonedParts, zonedWallClockToUtc } from "@/platform/kit";

const pad2 = (n: number) => String(n).padStart(2, "0");

// ---- civil-date grid helpers (operate on civil Dates; zone-free) ----
export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
export function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
export function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
export function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}
export function startOfWeekMonday(d: Date): Date {
  const r = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = (r.getDay() + 6) % 7;
  r.setDate(r.getDate() - dow);
  return r;
}

export function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** The `YYYY-MM-DD` of a CIVIL Date (its local Y/M/D). For an instant, use
 *  `isoDayKeyInZone`. */
export function isoDateKey(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

// Whole days between two local midnights, via Date.UTC so a DST transition
// crossed in between (which can make a plain ms-diff/86400000 land on a
// non-integer) never throws off the count.
function daysBetween(a: Date, b: Date): number {
  const utcA = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utcB = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((utcB - utcA) / 86_400_000);
}

/** Grid starting on the Monday on/before the 1st of the month, running just
 *  long enough (in whole weeks) to reach the month's last day — 4, 5 or 6
 *  weeks depending on the month, never a fixed 6. */
export function monthGrid(month: Date): Date[] {
  const start = startOfWeekMonday(startOfMonth(month));
  const totalDays = daysBetween(start, endOfMonth(month)) + 1; // inclusive of the last day
  const weeks = Math.ceil(totalDays / 7);
  return Array.from({ length: weeks * 7 }, (_, i) => addDays(start, i));
}

// ---- home-zone bridges (instant ↔ civil, always given the zone) ----

/** The CIVIL Date of "today" in the home zone — seeds the cursor, the selected
 *  day and the "today" highlight so they follow the setting, not the browser. */
export function homeToday(tz: string): Date {
  const [y, m, d] = isoDayInZone(new Date(), tz).split("-").map(Number);
  return new Date(y!, m! - 1, d!);
}

/** The `YYYY-MM-DD` an INSTANT falls on in the home zone. */
export function isoDayKeyInZone(isoInstant: string, tz: string): string {
  return isoDayInZone(new Date(isoInstant), tz);
}

// ---- event-modal / task-modal field helpers (home-zone wall clock) ----

/** Combine a home-zone date (YYYY-MM-DD) + time (HH:MM) into an ISO/UTC string. */
export function combineDateTime(date: string, time: string, tz: string): string {
  const [y, mo, d] = date.split("-").map(Number);
  const [h, mi] = (time || "00:00").split(":").map(Number);
  return zonedWallClockToUtc(y!, mo!, d!, h!, mi!, 0, tz).toISOString();
}

/** Split an ISO instant into home-zone { date, time } field values. */
export function splitDateTime(isoInstant: string, tz: string): { date: string; time: string } {
  const p = utcToZonedParts(new Date(isoInstant), tz);
  return {
    date: `${p.year}-${pad2(p.month)}-${pad2(p.day)}`,
    time: `${pad2(p.hour)}:${pad2(p.minute)}`,
  };
}

// Wall-clock field arithmetic — operates on the numbers the user sees and is
// therefore zone-free (adding 30 min to "09:00" is "09:30" in any zone). Kept
// as local-Date math within this sanctioned module.
/** Add minutes to a date+time, returning new { date, time }. */
export function addMinutesToDateTime(
  date: string,
  time: string,
  mins: number,
): { date: string; time: string } {
  const base = new Date(`${date}T${time || "09:00"}`);
  base.setMinutes(base.getMinutes() + mins);
  return {
    date: `${base.getFullYear()}-${pad2(base.getMonth() + 1)}-${pad2(base.getDate())}`,
    time: `${pad2(base.getHours())}:${pad2(base.getMinutes())}`,
  };
}

/** Minutes between two date+time pairs (wall-clock). */
export function minutesBetween(sDate: string, sTime: string, eDate: string, eTime: string): number {
  const a = new Date(`${sDate}T${sTime || "00:00"}`);
  const b = new Date(`${eDate}T${eTime || "00:00"}`);
  return Math.round((b.getTime() - a.getTime()) / 60000);
}

/** Human label for a duration in minutes, e.g. 90 -> "1h 30m". */
export function durationLabel(mins: number): string {
  if (mins <= 0) return "0 min";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h} ${h === 1 ? "hour" : "hours"}`;
  return `${m} min`;
}

export const DURATION_OPTIONS = [15, 30, 45, 60, 90, 120, 150, 180, 240, 300, 360, 480];

// ---- labels ----
// Pinned-locale date labels, used across the app so dates read one consistent
// way ("Tue, 30 Jun" / "30 Jun") instead of following the browser locale.
//
// `tz` is optional: pass it when `d` is an INSTANT (an event/feed timestamp) so
// the label lands in the home zone; omit it when `d` is a CIVIL Date (a grid
// cell / a YYYY-MM-DD parsed at local midnight), which has no zone to shift.
export function fmtDateLong(d: Date, tz?: string): string {
  return d.toLocaleDateString(APP_LOCALE, {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(tz ? { timeZone: tz } : {}),
  });
}
export function fmtDateShort(d: Date, tz?: string): string {
  return d.toLocaleDateString(APP_LOCALE, {
    month: "short",
    day: "numeric",
    ...(tz ? { timeZone: tz } : {}),
  });
}

/** 24-hour "HH:MM" time label in a specific IANA zone (falls back to the
 *  browser's local zone only if `timeZone` is omitted). `hour12` is explicit so
 *  a browser/OS 12-hour preference can never override it. */
export function fmtTime(d: Date, timeZone?: string): string {
  return d.toLocaleTimeString(APP_LOCALE, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    ...(timeZone ? { timeZone } : {}),
  });
}

/** Short relative-time label for a recent ISO timestamp, e.g. "3m", "5h", "2d".
 *  The relative part is zone-free; `tz` is used only for the >7d date fallback.
 *  For a full-word phrase (status lines, prose), use `timeAgoWords` instead. */
export function timeAgo(iso: string, tz: string): string {
  const diffMs = Date.now() - Date.parse(iso);
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return fmtDateShort(new Date(iso), tz);
}

/** Full-word relative-time phrase for a recent instant, e.g. "just now", "4 min ago",
 *  "2 hr ago", "3 d ago". For a compact label (table cells, badges), use `timeAgo` instead. */
export function timeAgoWords(iso: string): string {
  const mins = Math.floor((Date.now() - Date.parse(iso)) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  return `${days} d ago`;
}

/** Absolute date+time for a recent ISO INSTANT, rendered in the home zone —
 *  just the time if it's today there (that's what signals "today" to the
 *  reader), otherwise a short date plus time, optionally with the year. Always
 *  24-hour (see docs/design-system.md's "Dates & times"); `hour12` is explicit
 *  so a browser/OS 12-hour clock can never override it. */
/** Date and time; today's moments as the time alone unless `alwaysDate`. */
export function fmtDateTime(
  iso: string,
  tz: string,
  opts?: { withYear?: boolean; alwaysDate?: boolean },
): string {
  const d = new Date(iso);
  if (!opts?.alwaysDate && isoDayInZone(d, tz) === isoDayInZone(new Date(), tz)) {
    return d.toLocaleTimeString(APP_LOCALE, {
      hour: "numeric",
      minute: "2-digit",
      hour12: false,
      timeZone: tz,
    });
  }
  return d.toLocaleString(APP_LOCALE, {
    month: "short",
    day: "numeric",
    ...(opts?.withYear ? { year: "numeric" as const } : {}),
    hour: "numeric",
    minute: "2-digit",
    hour12: false,
    timeZone: tz,
  });
}

// ---- task due-date labels (shared by the sidebar Tasks widget and TasksPage) ----
type DueTone = "overdue" | "today" | "upcoming";

/** Classifies an ISO due-date instant relative to "today" in the home zone. */
export function dueTone(dueAt: string, tz: string): DueTone {
  const todayKey = isoDayInZone(new Date(), tz);
  const dueKey = isoDayInZone(new Date(dueAt), tz);
  if (dueKey < todayKey) return "overdue";
  if (dueKey === todayKey) return "today";
  return "upcoming";
}

/** Due-date label that reads "Today"/"Tomorrow" near the home-zone "now",
 *  otherwise a short date. */
export function fmtDueLabel(dueAt: string, tz: string): string {
  const tone = dueTone(dueAt, tz);
  if (tone === "today") return "Today";
  const tomorrowKey = isoDateKey(addDays(homeToday(tz), 1));
  if (isoDayInZone(new Date(dueAt), tz) === tomorrowKey) return "Tomorrow";
  return fmtDateShort(new Date(dueAt), tz);
}

/** A zone-less civil day, `YYYY-MM-DD`, as `3 Oct 2026` (or `3 Oct`). Read off
 *  the string, never through a `Date`, so no zone can move it to another day. */
export function fmtCivilDay(iso: string, { year = true }: { year?: boolean } = {}): string {
  const day = `${Number(iso.slice(8, 10))} ${MONTHS_SHORT[Number(iso.slice(5, 7)) - 1]}`;
  return year ? `${day} ${iso.slice(0, 4)}` : day;
}

/** A zone-less civil moment, `YYYY-MM-DDTHH:MM[:SS]`, as `3 Oct 2026, 9:05`:
 *  the time a document printed, read off the string as `fmtCivilDay` reads
 *  the day. A day alone reads as the day. */
export function fmtCivilDateTime(iso: string): string {
  const time = /^\d{4}-\d{2}-\d{2}[T ](\d{2}):(\d{2})/.exec(iso);
  const day = fmtCivilDay(iso);
  return time ? `${day}, ${Number(time[1])}:${time[2]}` : day;
}

/** A span still running, named as its whole unit: `2026 to date`. */
export const toDate = (name: string): string => `${name} to date`;

export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const MONTHS_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
export const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
